import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Typography,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export interface FAQItem {
  question: string;
  answer: string;
}

export interface EnhancedFAQSectionProps {
  faqData: FAQItem[];
}

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2),
  maxWidth: 760,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.65)",
  maxWidth: 640,
});

const Item = styled(Accordion)({
  marginBottom: 10,
  borderRadius: 10,
  border: "1px solid rgba(15, 23, 42, 0.1)",
  boxShadow: "none",
  backgroundColor: "#fff",
  overflow: "hidden",
  "&::before": { display: "none" },
  "&.Mui-expanded": {
    margin: "0 0 10px 0",
    borderColor: "rgba(47, 129, 173, 0.4)",
  },
});

const Summary = styled(AccordionSummary)({
  padding: "4px 22px",
  minHeight: 64,
  "& .MuiAccordionSummary-content": { margin: "16px 0" },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "#2f81ad",
    transition: "transform 0.25s ease",
  },
  "&.Mui-expanded": { minHeight: 64 },
});

const Details = styled(AccordionDetails)({
  padding: "0 22px 22px",
});

const Question = styled(Typography)({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#0f172a",
  lineHeight: 1.45,
});

const Answer = styled(Typography)({
  fontSize: "0.95rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.7)",
});

const EnhancedFAQSection: React.FC<EnhancedFAQSectionProps> = ({ faqData }) => {
  const [expanded, setExpanded] = useState<number | null>(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  return (
    <Box
      ref={ref}
      component="section"
      aria-labelledby="faq-heading"
      sx={{
        py: { xs: 8, md: 11 },
        backgroundColor: "#fff",
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            mb: { xs: 5, md: 7 },
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
          }}
        >
          <Label>FAQ</Label>
          <SectionTitle id="faq-heading" variant="h2">
            Questions we hear from new clients
          </SectionTitle>
          <Lead>
            Pricing, timelines, ownership, contracting. If you don't see your
            question, book a discovery call and we'll answer it directly.
          </Lead>
        </Box>

        <Box>
          {faqData.map((faq, i) => (
            <Item
              key={i}
              expanded={expanded === i}
              onChange={() => setExpanded(expanded === i ? null : i)}
            >
              <Summary expandIcon={<ExpandMoreIcon />}>
                <Question>{faq.question}</Question>
              </Summary>
              <Details>
                <Answer>{faq.answer}</Answer>
              </Details>
            </Item>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default EnhancedFAQSection;
